/**
 * PATH: packages/ui/src/components/settings/integrations-tab/IntegrationCard.tsx
 * PURPOSE: Renders a single integration with status badge, connection details, and action buttons
 * WHY: Extracted from IntegrationsTab.tsx to keep the main file under 300 lines
 *
 * FLOW:
 * ┌───────────────────┐    ┌──────────────────┐    ┌─────────────────────────┐
 * │ IntegrationStatus │ → │ IntegrationCard   │ → │ Card with status/actions │
 * └───────────────────┘    └──────────────────┘    └─────────────────────────┘
 *
 * DEPENDENCIES:
 * - lucide-react: icons for status badges and action buttons
 * - ../../lib/utils (cn): conditional classnames
 * - ./integrations-tab-types: IntegrationCardProps, IntegrationStatus
 */

import {
  Mail,
  Cloud,
  FileSpreadsheet,
  Briefcase,
  Check,
  X,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Loader2,
  Settings2,
  Zap,
  Clock,
  User,
  Brain,
  CreditCard,
  Map,
} from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { IntegrationCardProps } from './integrations-tab-types'

// =============================================================================
// ICON & COLOR MAPPING
// =============================================================================

const INTEGRATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  outlook: Mail,
  onedrive: Cloud,
  xero: FileSpreadsheet,
  simpro: Briefcase,
  gmail: Mail,
  deepseek: Brain,
  stripe: CreditCard,
  google_maps: Map,
}

const INTEGRATION_COLORS: Record<string, string> = {
  outlook: 'bg-blue-500',
  onedrive: 'bg-sky-500',
  xero: 'bg-emerald-500',
  simpro: 'bg-orange-500',
  gmail: 'bg-red-500',
  deepseek: 'bg-purple-500',
  stripe: 'bg-violet-500',
  google_maps: 'bg-green-500',
}

// =============================================================================
// HELPERS
// =============================================================================

function formatDate(dateStr?: string): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export function IntegrationCard({
  integration,
  onConnect,
  onDisconnect,
  onSync,
  onOpenSettings,
  isConnecting,
  isDisconnecting,
}: IntegrationCardProps) {
  const Icon = INTEGRATION_ICONS[integration.id.toLowerCase()] || Zap
  const colorClass = INTEGRATION_COLORS[integration.id.toLowerCase()] || 'bg-slate-500'

  // Support both `isConnected` boolean and `status` string
  const isConnected = integration.isConnected || integration.status === 'connected' || integration.status === 'expiring'
  const isPlanned = integration.status === 'planned'
  const isError = integration.status === 'error'
  const isExpiring = integration.status === 'expiring'

  return (
    <div className={cn(
      'p-4 rounded-xl border transition-colors',
      isConnected
        ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
        : isPlanned
          ? 'bg-slate-50/50 dark:bg-slate-800/30 border-dashed border-slate-200 dark:border-slate-700'
          : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
    )}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
          isPlanned ? 'bg-slate-300 dark:bg-slate-600' : colorClass,
        )}>
          <Icon className="w-6 h-6 text-white" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900 dark:text-white">
              {integration.name}
            </h4>
            {isConnected && !isExpiring && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Check className="w-3 h-3" />
                Connected
              </span>
            )}
            {isExpiring && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <AlertCircle className="w-3 h-3" />
                Token Expiring
              </span>
            )}
            {isError && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                <AlertCircle className="w-3 h-3" />
                Error
              </span>
            )}
            {isPlanned && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                Coming Soon
              </span>
            )}
            {!isConnected && !isPlanned && !isError && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                <X className="w-3 h-3" />
                Not connected
              </span>
            )}
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
            {integration.description}
          </p>

          {/* Connected details */}
          {isConnected && (
            <div className="space-y-1.5 mb-3">
              {integration.connectedBy && (
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <User className="w-3.5 h-3.5" />
                  <span>Connected by {integration.connectedBy}</span>
                </div>
              )}
              {integration.connectedEmail && !integration.connectedBy && (
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <Mail className="w-3.5 h-3.5" />
                  <span>{integration.connectedEmail}</span>
                </div>
              )}
              {integration.connectedAt && (
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Connected: {formatDate(integration.connectedAt)}</span>
                </div>
              )}
              {integration.lastSyncAt && (
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Last synced: {formatDate(integration.lastSyncAt)}</span>
                </div>
              )}
              {integration.syncStatus === 'error' && integration.syncError && (
                <div className="flex items-center gap-2 text-xs text-red-500">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>{integration.syncError}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                {onSync && (
                  <button
                    onClick={onSync}
                    disabled={integration.syncStatus === 'syncing'}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
                      'hover:bg-slate-200 dark:hover:bg-slate-600',
                      integration.syncStatus === 'syncing' && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    <RefreshCw className={cn('w-3.5 h-3.5', integration.syncStatus === 'syncing' && 'animate-spin')} />
                    {integration.syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
                  </button>
                )}
                {onOpenSettings && (
                  <button
                    onClick={onOpenSettings}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Settings
                  </button>
                )}
                {onDisconnect && (
                  <button
                    onClick={onDisconnect}
                    disabled={isDisconnecting}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20',
                      isDisconnecting && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    {isDisconnecting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                    Disconnect
                  </button>
                )}
              </>
            ) : (
              <>
                {onConnect && integration.isOAuth && (
                  <button
                    onClick={onConnect}
                    disabled={isConnecting}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      'bg-teal-500 text-white hover:bg-teal-600',
                      isConnecting && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    {isConnecting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4" />
                    )}
                    Connect
                  </button>
                )}
                {integration.isApiKey && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    API key configured in environment
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
